import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OPEN_BOOKING_INTAKE_EVENT } from "@/lib/marketingBooking";

type Lang = "en" | "de";

export const INTAKE_COPY = {
  en: {
    title: "Let's talk",
    intro: "Three quick questions so we can start right where you are. Then you pick your time right here.",
    step: (n: number, total: number) => `Step ${n} of ${total}`,
    next: "Next",
    back: "Back",
    levelQ: "How would you rate your current English level?",
    levels: [
      "A2 · I get by in simple situations",
      "B1 · Most everyday conversations work",
      "B2 · Conversations work, but they cost me energy",
      "C1+ · I'm fluent, but something is missing",
    ],
    frictionQ: "What is your biggest challenge when speaking English right now?",
    frictions: [
      "The words are there, but they vanish live",
      "I translate in my head and lose the thread",
      "I hold back to avoid mistakes",
      "I don't feel like myself in English",
    ],
    frictionOther: "Something else",
    frictionOtherPh: "Tell me in your own words…",
    whyQ: "Where does your English need to work?",
    whys: [
      "In meetings and presentations",
      "In everyday and social conversations",
      "For a project that matters to me",
      "For interviews or a new role",
    ],
    whyOther: "Somewhere else",
    whyOtherPh: "Tell me in your own words…",
    formatQ: "How would you like to talk?",
    phone: "Phone call",
    phoneNote: "Most people choose this",
    zoom: "Zoom video call",
    nameLabel: "Your first name",
    namePlaceholder: "Anna",
    emailLabel: "Your email",
    emailPlaceholder: "anna@example.com",
    phoneLabel: "Your phone number",
    phonePlaceholder: "079 123 45 67",
    submit: "Choose my time",
    submitting: "Saving your answers…",
    note: "Your answers go straight to Phil, so you only enter everything once.",
    doneTitle: "Almost done",
    doneBody: "Thanks, Phil has your answers. Pick a time below and choose phone or Zoom.",
    calTitle: "Choose your time",
    bookedTitle: "You're booked",
    bookedBody: "Our conversation is in the calendar. You'll get a confirmation email from Calendly. Phil looks forward to talking with you.",
    backHome: "Back to the homepage",
    edit: "Change my answers",
    doneAgain: "Open Calendly again",
    required: "Please fill in all fields.",
    invalidEmail: "Please enter a valid email address.",
  },
  de: {
    title: "Lass uns sprechen",
    intro: "Drei kurze Fragen, damit wir genau da ansetzen, wo du stehst. Danach wählst du gleich hier deinen Termin.",
    step: (n: number, total: number) => `Schritt ${n} von ${total}`,
    next: "Weiter",
    back: "Zurück",
    levelQ: "Wie schätzt du dein derzeitiges Englischniveau ein?",
    levels: [
      "A2 · Ich komme in einfachen Situationen zurecht",
      "B1 · Die meisten Alltagsgespräche klappen",
      "B2 · Gespräche klappen, aber sie kosten mich Energie",
      "C1+ · Ich bin fliessend, aber etwas fehlt",
    ],
    frictionQ: "Was ist im Moment deine grösste Herausforderung beim Englischsprechen?",
    frictions: [
      "Die Worte sind da, aber live verschwinden sie",
      "Ich übersetze im Kopf und verliere den Faden",
      "Ich halte mich zurück, um Fehler zu vermeiden",
      "Ich fühle mich auf Englisch nicht wie ich selbst",
    ],
    frictionOther: "Etwas anderes",
    frictionOtherPh: "Beschreibe es in deinen eigenen Worten…",
    whyQ: "Wo soll dein Englisch funktionieren?",
    whys: [
      "In Meetings und Präsentationen",
      "In Alltags- und Smalltalk-Situationen",
      "Für ein Projekt, das mir wichtig ist",
      "Für Bewerbungen oder eine neue Rolle",
    ],
    whyOther: "Woanders",
    whyOtherPh: "Beschreibe es in deinen eigenen Worten…",
    formatQ: "Wie möchtest du sprechen?",
    phone: "Telefonanruf",
    phoneNote: "Die meisten wählen das",
    zoom: "Zoom-Videocall",
    nameLabel: "Dein Vorname",
    namePlaceholder: "Anna",
    emailLabel: "Deine E-Mail",
    emailPlaceholder: "anna@example.ch",
    phoneLabel: "Deine Telefonnummer",
    phonePlaceholder: "079 123 45 67",
    submit: "Meinen Termin wählen",
    submitting: "Deine Antworten werden gespeichert…",
    note: "Deine Antworten gehen direkt an Phil, du gibst alles nur einmal ein.",
    doneTitle: "Fast geschafft",
    doneBody: "Danke, Phil hat deine Antworten. Wähle unten einen Termin und ob Telefon oder Zoom.",
    calTitle: "Wähle deinen Termin",
    bookedTitle: "Dein Termin steht",
    bookedBody: "Unser Gespräch ist im Kalender. Du bekommst eine Bestätigung per E-Mail von Calendly. Phil freut sich auf das Gespräch.",
    backHome: "Zur Startseite",
    edit: "Antworten ändern",
    doneAgain: "Calendly erneut öffnen",
    required: "Bitte fülle alle Felder aus.",
    invalidEmail: "Bitte gib eine gültige E-Mail-Adresse ein.",
  },
} as const;

export const TOTAL_STEPS = 3;

export function detectLang(): Lang {
  if (typeof window === "undefined") return "en";
  if (window.location.pathname.startsWith("/de")) return "de";
  // Mirror the homepage: it switches to German via stored choice or browser
  // language without changing the URL, so the dialog must follow the same signals.
  try {
    const stored = localStorage.getItem("start-lang");
    if (stored === "en" || stored === "de") return stored;
  } catch {}
  return navigator.language?.toLowerCase().startsWith("de") ? "de" : "en";
}

/** Routes every "Let's talk" button to the dedicated booking page. */
export function BookingIntakeDialog() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => navigate(detectLang() === "de" ? "/de/expression-check" : "/expression-check");
    window.addEventListener(OPEN_BOOKING_INTAKE_EVENT, handler);
    return () => window.removeEventListener(OPEN_BOOKING_INTAKE_EVENT, handler);
  }, [navigate]);
  return null;
}
